

import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Task, Priority } from "@/stores/taskStore";
import { cn } from "@/lib/utils";
import { Calendar, User, Bot, GripVertical } from "lucide-react";

interface TaskCardProps {
  task: Task;
  onClick?: () => void;
}

const priorityColors: Record<Priority, string> = {
  LOW: "bg-blue-500",
  MEDIUM: "bg-yellow-500",
  HIGH: "bg-orange-500",
  URGENT: "bg-red-500",
};

export function TaskCard({ task, onClick }: TaskCardProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: task.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleClick = (e: React.MouseEvent) => {
    if (!isDragging && onClick) {
      onClick();
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "bg-white dark:bg-zinc-900 rounded-lg border shadow-sm p-3 cursor-grab active:cursor-grabbing hover:shadow-md transition-shadow",
        isDragging && "opacity-50 z-50"
      )}
      onClick={handleClick}
      {...attributes}
      {...listeners}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm truncate">{task.title}</h4>
          {task.description && (
            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
              {task.description}
            </p>
          )}
        </div>
        <div className="cursor-grab active:cursor-grabbing p-1 -m-1 text-muted-foreground hover:text-foreground">
          <GripVertical className="w-4 h-4" />
        </div>
      </div>

      {task.progress > 0 && (
        <div className="mt-2">
          <div className="h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${task.progress}%` }}
            />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span
          className={cn(
            "w-2 h-2 rounded-full",
            priorityColors[task.priority]
          )}
          title={task.priority}
        />

        {task.dueDate && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Calendar className="w-3 h-3" />
            {new Date(task.dueDate).toLocaleDateString()}
          </div>
        )}

        {task.assigneeUser && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            {task.assigneeUser.image ? (
              <img
                src={task.assigneeUser.image}
                alt=""
                className="w-4 h-4 rounded-full"
              />
            ) : (
              <User className="w-3 h-3" />
            )}
            <span className="truncate max-w-[60px]">
              {task.assigneeUser.name || task.assigneeUser.email}
            </span>
          </div>
        )}

        {task.assigneeAgent && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <Bot className="w-3 h-3" />
            <span>{task.assigneeAgent}</span>
          </div>
        )}
      </div>
    </div>
  );
}
